/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import teamsRepository from 'modules/teams/domain/repositories/teams.repository';
import { KitElement } from 'modules/teams/domain/enums/kit-element';
import { KitPattern, KIT_PATTERN_COLOR_COUNT, KIT_PATTERN_LABELS } from 'modules/teams/domain/enums/kit-pattern';
import { KitType } from 'modules/teams/domain/enums/kit-type';
import { Team } from 'modules/teams/domain/interfaces/team';
import { TeamKit, TeamKitElement } from 'modules/teams/domain/interfaces/team-kit';
import { upsertTeamKits } from 'modules/teams/store/teams';
import UDButton from 'ud-ui/components/button';
import UDText from 'ud-ui/components/text';
import {
  KIT_VIEWBOX,
  KIT_SHIRT_PATH,
  KIT_SHIRT_TRANSFORM,
  KIT_SHORTS_PATH,
  KIT_SOCKS_PATH,
  KIT_SHIRT_PATTERN_BOX,
  KIT_SHORTS_PATTERN_BOX,
  KIT_SOCKS_PATTERN_BOX,
  KIT_STROKE,
} from './kit-wikimedia-paths';
import { AppDispatch } from 'store/store';
import { theme } from 'styles/theme/theme';

const KIT_TYPE_LABELS: Record<KitType, string> = {
  [KitType.HOME]: 'Домашняя',
  [KitType.AWAY]: 'Гостевая',
  [KitType.THIRD]: 'Третья',
  [KitType.GOALKEEPER]: 'Вратарская',
};

const KIT_ELEMENT_LABELS: Record<KitElement, string> = {
  [KitElement.SHIRT]: 'Футболка',
  [KitElement.SHORTS]: 'Шорты',
  [KitElement.SOCKS]: 'Гетры',
  [KitElement.CAP]: 'Кепка',
  [KitElement.HELMET]: 'Шлем',
};

const ALL_KIT_TYPES = [KitType.HOME, KitType.AWAY, KitType.THIRD, KitType.GOALKEEPER];
const DEFAULT_COLOR = theme.colors.SurfaceWhite;
const DEFAULT_COLOR_2 = '#000000';
const DEFAULT_PATTERN = KitPattern.SOLID;

interface ElementState {
  pattern: KitPattern;
  colors: string[];
}

type KitState = Record<KitElement, ElementState>;
type AllKitState = Record<KitType, KitState>;

function makeDefaultElementState(pattern = DEFAULT_PATTERN): ElementState {
  const count = KIT_PATTERN_COLOR_COUNT[pattern];
  return { pattern, colors: count === 1 ? [DEFAULT_COLOR] : [DEFAULT_COLOR, DEFAULT_COLOR_2] };
}

function buildInitialState(kits: TeamKit[], allowedElements: KitElement[]): AllKitState {
  const initial = {} as AllKitState;
  for (const kitType of ALL_KIT_TYPES) {
    const kitState = {} as KitState;
    for (const el of allowedElements) {
      kitState[el] = makeDefaultElementState();
    }
    const existingKit = kits.find((k) => k.type === kitType);
    if (existingKit) {
      for (const kitEl of existingKit.elements) {
        if (allowedElements.includes(kitEl.element)) {
          kitState[kitEl.element] = {
            pattern: kitEl.attributes.pattern,
            colors: [...kitEl.attributes.colors],
          };
        }
      }
    }
    initial[kitType] = kitState;
  }
  return initial;
}

interface KitEditorProps {
  team: Team;
}

const KitEditor: React.FC<KitEditorProps> = ({ team }) => {
  const dispatch = useDispatch<AppDispatch>();

  const [allowedElements, setAllowedElements] = useState<KitElement[]>([]);
  const [kitState, setKitState] = useState<AllKitState>({} as AllKitState);
  const [activeKitType, setActiveKitType] = useState<KitType>(KitType.HOME);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingElements, setIsLoadingElements] = useState(false);

  useEffect(() => {
    if (!team.sportType) return;

    setIsLoadingElements(true);
    teamsRepository
      .getSportKitElements(team.sportType)
      .then((elements) => {
        const validElements = elements.filter((e): e is KitElement => Object.values(KitElement).includes(e as KitElement));
        setAllowedElements(validElements);
        setKitState(buildInitialState(team.kits ?? [], validElements));
      })
      .catch(() => {
        toast.error('Не удалось загрузить элементы формы');
      })
      .finally(() => {
        setIsLoadingElements(false);
      });
  }, [team.sportType, team.kits]);

  const handlePatternChange = useCallback(
    (element: KitElement, pattern: KitPattern) => {
      const requiredCount = KIT_PATTERN_COLOR_COUNT[pattern];
      setKitState((prev) => {
        const current = prev[activeKitType]?.[element] ?? makeDefaultElementState();
        const newColors = [...current.colors];
        // Expand or trim to required count
        while (newColors.length < requiredCount) newColors.push(DEFAULT_COLOR_2);
        newColors.splice(requiredCount);
        return {
          ...prev,
          [activeKitType]: {
            ...prev[activeKitType],
            [element]: { pattern, colors: newColors },
          },
        };
      });
    },
    [activeKitType],
  );

  const handleColorChange = useCallback(
    (element: KitElement, colorIndex: number, color: string) => {
      setKitState((prev) => {
        const current = prev[activeKitType]?.[element] ?? makeDefaultElementState();
        const newColors = [...current.colors];
        newColors[colorIndex] = color;
        return {
          ...prev,
          [activeKitType]: {
            ...prev[activeKitType],
            [element]: { ...current, colors: newColors },
          },
        };
      });
    },
    [activeKitType],
  );

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const kits = ALL_KIT_TYPES.map((kitType) => ({
        type: kitType,
        elements: allowedElements.map((el) => {
          const state = kitState[kitType]?.[el] ?? makeDefaultElementState();
          return {
            element: el,
            pattern: state.pattern,
            colors: state.colors,
          };
        }),
      }));

      await dispatch(upsertTeamKits({ teamId: team.id, kits })).unwrap();
      toast.success('Форма команды сохранена');
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast.error(`Ошибка сохранения: ${message}`);
    } finally {
      setIsSaving(false);
    }
  }, [allowedElements, kitState, dispatch, team.id]);

  if (!team.sportType) {
    return (
      <div className="py-6">
        <UDText type="body-text">Тип спорта не определён для данной команды.</UDText>
      </div>
    );
  }

  if (isLoadingElements) {
    return (
      <div className="flex flex-col gap-4 py-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="skeleton-block" style={{ width: '100%', height: 64 }} />
        ))}
      </div>
    );
  }

  if (allowedElements.length === 0) {
    return (
      <div className="py-6">
        <UDText type="body-text">Для данного вида спорта элементы формы не определены.</UDText>
      </div>
    );
  }

  const activeKitElements = kitState[activeKitType] ?? {};

  return (
    <div className="flex flex-col gap-6">
      {/* Kit type tabs */}
      <div
        css={css`
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        `}
      >
        {ALL_KIT_TYPES.map((kitType) => (
          <button
            key={kitType}
            type="button"
            onClick={() => setActiveKitType(kitType)}
            css={css`
              padding: 8px 20px;
              border-radius: 8px;
              border: 2px solid ${activeKitType === kitType ? '#f5a623' : '#e0e0e0'};
              background: ${activeKitType === kitType ? '#fff8eb' : theme.colors.SurfaceWhite};
              font-weight: ${activeKitType === kitType ? 600 : 400};
              cursor: pointer;
              transition: all 0.15s;
              &:hover {
                border-color: #f5a623;
              }
            `}
          >
            <KitSwatch elements={allowedElements} elementStates={kitState[kitType] ?? {}} />
            <UDText type="subhead">{KIT_TYPE_LABELS[kitType]}</UDText>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Left: pattern + color controls */}
        <div className="col-span-7 flex flex-col gap-6">
          {allowedElements.map((element) => {
            const state = activeKitElements[element] ?? makeDefaultElementState();
            const { pattern, colors } = state;
            const requiredCount = KIT_PATTERN_COLOR_COUNT[pattern];
            return (
              <div key={element} className="flex flex-col gap-2">
                <UDText type="subhead">{KIT_ELEMENT_LABELS[element]}</UDText>
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    flex-wrap: wrap;
                  `}
                >
                  {/* Pattern selector */}
                  <select
                    value={pattern}
                    onChange={(e) => handlePatternChange(element, e.target.value as KitPattern)}
                    css={css`
                      padding: 8px 12px;
                      border: 1px solid #e0e0e0;
                      border-radius: 8px;
                      font-size: 14px;
                      cursor: pointer;
                      &:focus {
                        outline: none;
                        border-color: #f5a623;
                      }
                    `}
                  >
                    {Object.values(KitPattern).map((p) => (
                      <option key={p} value={p}>
                        {KIT_PATTERN_LABELS[p]}
                      </option>
                    ))}
                  </select>

                  {/* Color inputs — one per required color */}
                  {Array.from({ length: requiredCount }, (_, idx) => (
                    <div
                      key={idx}
                      css={css`
                        display: flex;
                        align-items: center;
                        gap: 6px;
                      `}
                    >
                      <input
                        type="color"
                        value={colors[idx] ?? DEFAULT_COLOR}
                        onChange={(e) => handleColorChange(element, idx, e.target.value)}
                        css={css`
                          width: 40px;
                          height: 40px;
                          border: none;
                          border-radius: 6px;
                          cursor: pointer;
                          padding: 2px;
                          background: transparent;
                        `}
                        title={`Цвет ${idx + 1}`}
                      />
                      <input
                        type="text"
                        value={(colors[idx] ?? DEFAULT_COLOR).toUpperCase()}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (/^#[0-9A-Fa-f]{0,6}$/.test(val)) {
                            handleColorChange(element, idx, val);
                          }
                        }}
                        maxLength={7}
                        css={css`
                          width: 90px;
                          padding: 6px 10px;
                          border: 1px solid #e0e0e0;
                          border-radius: 8px;
                          font-family: monospace;
                          font-size: 13px;
                          text-transform: uppercase;
                          &:focus {
                            outline: none;
                            border-color: #f5a623;
                          }
                        `}
                      />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Right: live SVG preview */}
        <div className="col-span-5 flex justify-center">
          <KitPreview elementStates={activeKitElements} allowedElements={allowedElements} />
        </div>
      </div>

      <div className="mt-2 flex gap-4">
        <UDButton type="button" variant="primary" onClick={handleSave} loading={isSaving} disabled={isSaving} className="w-48">
          Сохранить форму
        </UDButton>
      </div>
    </div>
  );
};

// ── KitSwatch ──────────────────────────────────────────────────────────────

interface KitSwatchProps {
  elements: KitElement[];
  elementStates: Partial<KitState>;
}

const KitSwatch: React.FC<KitSwatchProps> = ({ elements, elementStates }) => (
  <div
    css={css`
      display: flex;
      gap: 4px;
      margin-bottom: 4px;
      justify-content: center;
    `}
  >
    {elements.slice(0, 3).map((el) => {
      const state = elementStates[el];
      const primaryColor = state?.colors[0] ?? DEFAULT_COLOR;
      const secondaryColor = state?.colors[1];
      const pattern = state?.pattern ?? KitPattern.SOLID;
      return (
        <svg key={el} width="14" height="14" viewBox="0 0 14 14">
          <rect width="14" height="14" rx="2" fill={primaryColor} stroke="rgba(0,0,0,0.15)" strokeWidth="1" />
          {pattern !== KitPattern.SOLID && secondaryColor && (
            <PatternOverlay pattern={pattern} color2={secondaryColor} width={14} height={14} rx={2} />
          )}
        </svg>
      );
    })}
  </div>
);

// ── PatternOverlay (SVG helper) ────────────────────────────────────────────

interface PatternOverlayProps {
  pattern: KitPattern;
  color2: string;
  width: number;
  height: number;
  rx?: number;
}

const PatternOverlay: React.FC<PatternOverlayProps> = ({ pattern, color2, width, height, rx = 0 }) => {
  const clipId = `clip-${pattern}-${Math.random().toString(36).slice(2, 7)}`;

  const renderShape = () => {
    switch (pattern) {
      case KitPattern.STRIPES_VERTICAL:
        return (
          <>
            <rect x={width * 0.33} y="0" width={width * 0.34} height={height} fill={color2} />
          </>
        );
      case KitPattern.STRIPES_HORIZONTAL:
        return <rect x="0" y={height * 0.33} width={width} height={height * 0.34} fill={color2} />;
      case KitPattern.HALVES:
        return <rect x={width / 2} y="0" width={width / 2} height={height} fill={color2} />;
      case KitPattern.QUARTERS:
        return (
          <>
            <rect x="0" y="0" width={width / 2} height={height / 2} fill={color2} />
            <rect x={width / 2} y={height / 2} width={width / 2} height={height / 2} fill={color2} />
          </>
        );
      case KitPattern.SASH:
        return <polygon points={`${width * 0.3},0 ${width},0 ${width * 0.7},${height} 0,${height}`} fill={color2} />;
      case KitPattern.CHECKERED: {
        const cw = width / 3;
        const ch = height / 3;
        return (
          <>
            {[0, 1, 2].flatMap((row) =>
              [0, 1, 2].map((col) =>
                (row + col) % 2 === 1 ? (
                  <rect key={`${row}-${col}`} x={col * cw} y={row * ch} width={cw} height={ch} fill={color2} />
                ) : null,
              ),
            )}
          </>
        );
      }
      case KitPattern.GRADIENT:
        return (
          <>
            <defs>
              <linearGradient id={`grad-${clipId}`} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="transparent" />
                <stop offset="100%" stopColor={color2} />
              </linearGradient>
            </defs>
            <rect x="0" y="0" width={width} height={height} fill={`url(#grad-${clipId})`} />
          </>
        );
      case KitPattern.PINSTRIPES:
        return (
          <>
            {[1, 3, 5].map((x) => (
              <rect key={x} x={(x / 6) * width - 0.5} y="0" width="1" height={height} fill={color2} opacity="0.7" />
            ))}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <defs>
        <clipPath id={clipId}>
          <rect x="0" y="0" width={width} height={height} rx={rx} />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>{renderShape()}</g>
    </>
  );
};

// ── KitPreview ──────────────────────────────────────────────────────────────

interface KitPreviewProps {
  elementStates: Partial<KitState>;
  allowedElements: KitElement[];
}

interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

// Generic pattern fill that draws within an arbitrary bounding box (clipped to a garment shape by the caller).
const GarmentPattern: React.FC<{ pattern: KitPattern; color2: string; box: Box; id: string }> = ({ pattern, color2, box, id }) => {
  const { x, y, w, h } = box;
  switch (pattern) {
    case KitPattern.STRIPES_VERTICAL: {
      const n = 7;
      const sw = w / n;
      return (
        <>
          {Array.from({ length: n }).map((_, i) =>
            i % 2 === 1 ? <rect key={i} x={x + i * sw} y={y} width={sw} height={h} fill={color2} /> : null,
          )}
        </>
      );
    }
    case KitPattern.STRIPES_HORIZONTAL: {
      const n = 7;
      const sh = h / n;
      return (
        <>
          {Array.from({ length: n }).map((_, i) =>
            i % 2 === 1 ? <rect key={i} x={x} y={y + i * sh} width={w} height={sh} fill={color2} /> : null,
          )}
        </>
      );
    }
    case KitPattern.HALVES:
      return <rect x={x + w / 2} y={y} width={w / 2} height={h} fill={color2} />;
    case KitPattern.QUARTERS:
      return (
        <>
          <rect x={x} y={y} width={w / 2} height={h / 2} fill={color2} />
          <rect x={x + w / 2} y={y + h / 2} width={w / 2} height={h / 2} fill={color2} />
        </>
      );
    case KitPattern.SASH:
      return (
        <polygon points={`${x + w * 0.18},${y} ${x + w * 0.5},${y} ${x + w * 0.82},${y + h} ${x + w * 0.5},${y + h}`} fill={color2} />
      );
    case KitPattern.CHECKERED: {
      const cols = 4;
      const rows = 5;
      const cw = w / cols;
      const ch = h / rows;
      return (
        <>
          {Array.from({ length: rows }).flatMap((_, r) =>
            Array.from({ length: cols }).map((__, c) =>
              (r + c) % 2 === 1 ? <rect key={`${r}-${c}`} x={x + c * cw} y={y + r * ch} width={cw} height={ch} fill={color2} /> : null,
            ),
          )}
        </>
      );
    }
    case KitPattern.GRADIENT:
      return (
        <>
          <defs>
            <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="transparent" />
              <stop offset="100%" stopColor={color2} stopOpacity="0.85" />
            </linearGradient>
          </defs>
          <rect x={x} y={y} width={w} height={h} fill={`url(#${id})`} />
        </>
      );
    case KitPattern.PINSTRIPES: {
      const n = 9;
      const gap = w / n;
      return (
        <>
          {Array.from({ length: n }).map((_, i) => (
            <rect key={i} x={x + i * gap + gap / 2} y={y} width={1.4} height={h} fill={color2} opacity={0.55} />
          ))}
        </>
      );
    }
    default:
      return null;
  }
};

// ── KitPreview (Wikimedia Kit_blank.svg template) ───────────────────────────

const KitPreview: React.FC<KitPreviewProps> = ({ elementStates, allowedElements }) => {
  const shirt = elementStates[KitElement.SHIRT] ?? makeDefaultElementState();
  const shorts = elementStates[KitElement.SHORTS] ?? makeDefaultElementState();
  const socks = elementStates[KitElement.SOCKS] ?? makeDefaultElementState();

  const hasShirt = allowedElements.includes(KitElement.SHIRT);
  const hasShorts = allowedElements.includes(KitElement.SHORTS);
  const hasSocks = allowedElements.includes(KitElement.SOCKS);

  const shirtColor = shirt.colors[0] ?? DEFAULT_COLOR;
  const shortsColor = shorts.colors[0] ?? DEFAULT_COLOR;
  const socksColor = socks.colors[0] ?? DEFAULT_COLOR;

  return (
    <div
      css={css`
        width: 100%;
        display: flex;
        justify-content: center;
      `}
    >
      <svg viewBox={KIT_VIEWBOX} width="100%" style={{ maxWidth: 220 }} role="img" aria-label="Предпросмотр формы">
        <rect width="100" height="135" rx="8" fill="#f4f5f7" />

        {/* Draw order matches Kit_blank.svg: socks → shorts → shirt */}
        {hasSocks && (
          <g>
            <defs>
              <clipPath id="clip-socks">
                <path d={KIT_SOCKS_PATH} />
              </clipPath>
            </defs>
            <path d={KIT_SOCKS_PATH} fill={socksColor} {...KIT_STROKE} />
            {socks.pattern !== KitPattern.SOLID && socks.colors[1] && (
              <g clipPath="url(#clip-socks)">
                <GarmentPattern pattern={socks.pattern} color2={socks.colors[1]} box={KIT_SOCKS_PATTERN_BOX} id="pat-socks" />
              </g>
            )}
          </g>
        )}

        {hasShorts && (
          <g>
            <defs>
              <clipPath id="clip-shorts">
                <path d={KIT_SHORTS_PATH} />
              </clipPath>
            </defs>
            <path d={KIT_SHORTS_PATH} fill={shortsColor} {...KIT_STROKE} />
            {shorts.pattern !== KitPattern.SOLID && shorts.colors[1] && (
              <g clipPath="url(#clip-shorts)">
                <GarmentPattern pattern={shorts.pattern} color2={shorts.colors[1]} box={KIT_SHORTS_PATTERN_BOX} id="pat-shorts" />
              </g>
            )}
          </g>
        )}

        {hasShirt && (
          <g transform={KIT_SHIRT_TRANSFORM}>
            <defs>
              <clipPath id="clip-shirt">
                <path d={KIT_SHIRT_PATH} />
              </clipPath>
            </defs>
            <path d={KIT_SHIRT_PATH} fill={shirtColor} {...KIT_STROKE} />
            {shirt.pattern !== KitPattern.SOLID && shirt.colors[1] && (
              <g clipPath="url(#clip-shirt)">
                <GarmentPattern pattern={shirt.pattern} color2={shirt.colors[1]} box={KIT_SHIRT_PATTERN_BOX} id="pat-shirt" />
              </g>
            )}
          </g>
        )}
      </svg>
    </div>
  );
};

export default KitEditor;
